import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Filter, Plus, Trash2, Users as UsersIcon, Eye } from "lucide-react";

const AUDIENCES = [
  { value: "clients", label: "Clients" },
  { value: "leads", label: "Leads" },
  { value: "marketing_contacts", label: "Marketing contacts" },
];

const CUSTOMER_TYPES = ["prospect", "customer", "distributor", "partner"];
const LEAD_STATUSES = ["new", "contacted", "qualified", "converted", "dropped"];

function emptyForm() {
  return {
    name: "",
    audience: "clients",
    description: "",
    filters: {
      q: "", industry: "", location: "", customer_type: "", status: "",
      source: "", last_interaction_days: "", categories: [],
    },
  };
}

function SegmentDialog({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    if (open) api.get("/marketing-contact-categories").then(({ data }) => setCats(data || [])).catch(() => setCats([]));
  }, [open]);
  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          audience: initial.audience || "clients",
          description: initial.description || "",
          filters: { ...emptyForm().filters, ...(initial.filters || {}) },
        });
      } else {
        setForm(emptyForm());
      }
    }
  }, [open, initial]);

  const setFilter = (k, v) => setForm((f) => ({ ...f, filters: { ...f.filters, [k]: v } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Segment name is required"); return; }
    const cleanFilters = {};
    Object.entries(form.filters).forEach(([k, v]) => {
      if (Array.isArray(v)) { if (v.length) cleanFilters[k] = v; }
      else if (v !== "" && v !== null && v !== undefined) cleanFilters[k] = v;
    });
    const payload = {
      name: form.name.trim(),
      audience: form.audience,
      description: form.description.trim() || null,
      filters: cleanFilters,
    };
    setSaving(true);
    try {
      if (initial?.id) {
        await api.patch(`/segments/${initial.id}`, { name: payload.name, description: payload.description, filters: payload.filters });
      } else {
        await api.post("/segments", payload);
      }
      toast.success(initial?.id ? "Segment updated" : "Segment saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save segment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="segment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {initial?.id ? "Edit segment" : "New segment"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="seg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hyderabad cement plants" />
          </div>
          <div className="space-y-1.5">
            <Label>Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })} disabled={!!initial?.id}>
              <SelectTrigger data-testid="seg-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {initial?.id && <p className="text-[11px] text-muted-foreground">Audience is locked once saved — create a new segment to switch.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Cement majors in Telangana with ₹10Cr+ ARR" />
          </div>

          <div className="pt-2 border-t border-border/60">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Filters</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Search (name · email · company)</Label>
                <Input data-testid="seg-q" value={form.filters.q} onChange={(e) => setFilter("q", e.target.value)} placeholder="cement" />
              </div>
              <div className="space-y-1.5">
                <Label>Industry</Label>
                <Input value={form.filters.industry} onChange={(e) => setFilter("industry", e.target.value)} placeholder="Cement" />
              </div>
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Input value={form.filters.location} onChange={(e) => setFilter("location", e.target.value)} placeholder="Hyderabad" />
              </div>
              {form.audience === "clients" && (
                <div className="space-y-1.5">
                  <Label>Customer type</Label>
                  <Select value={form.filters.customer_type || "any"} onValueChange={(v) => setFilter("customer_type", v === "any" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      {CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.audience === "leads" && (
                <div className="space-y-1.5">
                  <Label>Lead status</Label>
                  <Select value={form.filters.status || "any"} onValueChange={(v) => setFilter("status", v === "any" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.audience === "marketing_contacts" && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Marketing categories (leave empty = all)</Label>
                  <div className="flex flex-wrap gap-1" data-testid="seg-categories">
                    {cats.map((c) => {
                      const on = (form.filters.categories || []).includes(c.name);
                      return (
                        <button key={c.id} type="button"
                          onClick={() => setFilter("categories", on ? (form.filters.categories || []).filter((x) => x !== c.name) : [...(form.filters.categories || []), c.name])}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70 hover:border-primary/50"}`}
                          data-testid={`seg-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Source (leads / marketing)</Label>
                <Input value={form.filters.source} onChange={(e) => setFilter("source", e.target.value)} placeholder="conference" />
              </div>
              <div className="space-y-1.5">
                <Label>Interacted in last N days</Label>
                <Input type="number" min="0" value={form.filters.last_interaction_days} onChange={(e) => setFilter("last_interaction_days", e.target.value)} placeholder="30" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="seg-save">
            {saving ? "Saving…" : initial?.id ? "Save changes" : "Create segment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ seg, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!seg) return;
    setLoading(true);
    api.get(`/segments/${seg.id}/preview?limit=200`).then(({ data }) => {
      setRows(data.rows || []);
    }).catch(() => setRows([])).finally(() => setLoading(false));
  }, [seg]);
  return (
    <Dialog open={!!seg} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="segment-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Eye className="h-4 w-4" /> {seg?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading preview…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No contacts match this filter right now.</p>
        ) : (
          <div className="divide-y divide-border/60 max-h-[60vh] overflow-y-auto">
            {rows.map((r) => (
              <div key={r.id} className="py-2.5 flex items-start gap-3">
                <div className="h-8 w-8 rounded-xl bg-primary-50 text-primary-700 grid place-items-center shrink-0 text-xs font-bold">
                  {((r.company_name || r.name || "—")[0] || "—").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{r.company_name || r.name || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.contact_person || r.name || ""}
                    {r.phone ? ` · ${r.phone}` : ""}
                    {r.email ? ` · ${r.email}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SegmentsPanel() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewSeg, setPreviewSeg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/segments");
      setSegments(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (!window.confirm(`Delete segment "${s.name}"?`)) return;
    try {
      await api.delete(`/segments/${s.id}`);
      toast.success("Segment deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const audLabel = (a) => AUDIENCES.find((x) => x.value === a)?.label || a;

  return (
    <div data-testid="segments-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Segments</p>
          <h2 className="font-display text-2xl font-black tracking-tight">Your audiences</h2>
          <p className="text-xs text-muted-foreground mt-1">Saved filters you own. Others can't see them — your outreach lists, your rules.</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-segment-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New segment
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading segments…</p>
      ) : segments.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-border/60">
          <CardContent className="py-12 text-center">
            <Filter className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold">No segments yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Create a segment to target outreach to the right contacts.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {segments.map((s) => (
            <Card key={s.id} className="rounded-3xl border-border/60 shadow-soft card-lift" data-testid={`segment-${s.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.description || "—"}</p>
                  </div>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider shrink-0">{audLabel(s.audience)}</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <UsersIcon className="h-4 w-4 text-primary" />
                  <span className="font-display font-black text-xl">{s.match_count ?? 0}</span>
                  <span className="text-xs text-muted-foreground">matching</span>
                </div>
                <div className="flex items-center gap-1.5 pt-2 border-t border-border/60">
                  <Button variant="ghost" size="sm" onClick={() => setPreviewSeg(s)} data-testid={`seg-preview-${s.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setDialogOpen(true); }} data-testid={`seg-edit-${s.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(s)} className="text-red-600 hover:text-red-700 ml-auto" data-testid={`seg-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SegmentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} initial={editing} />
      <PreviewDialog seg={previewSeg} onClose={() => setPreviewSeg(null)} />
    </div>
  );
}
